import React, { useCallback, useState } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import ClearIcon from '@material-ui/icons/Clear';
import { Button, useListContext, useNotify } from 'react-admin';
import { useDispatch } from 'react-redux';
import QrScannerDialog from '../../qr/QrScannerDialog';
import { useFindAvatarByQr } from '../hooks/useFindAvatarByQr';
import { selectAnc } from '../../../redux/actions/ancs';

const QR_FILTER_KEY = 'mcs.yourls.short';

const QrScanButton: React.FC = () => {
    const notify = useNotify();
    const dispatch = useDispatch();
    const find = useFindAvatarByQr();
    const { setFilters, displayedFilters, filterValues } = useListContext();

    const [open, setOpen] = useState(false);
    const [processing, setProcessing] = useState(false);

    // El filtro por QR (mcs.yourls.short) no aparece como input visible, así que
    // mostramos un chip para limpiarlo y volver a ver toda la lista.
    const isQrFiltered = !!(filterValues && filterValues[QR_FILTER_KEY]);
    const clearQrFilter = useCallback(() => {
        const next = { ...filterValues };
        delete next[QR_FILTER_KEY];
        setFilters(next, displayedFilters);
    }, [filterValues, displayedFilters, setFilters]);

    const handleDecoded = useCallback(
        async (decodedText: string) => {
            setOpen(false);
            setProcessing(true);
            try {
                const { record, short } = await find(decodedText);
                if (record) {
                    setFilters({ ...filterValues, [QR_FILTER_KEY]: short }, displayedFilters);
                    dispatch(selectAnc(record));
                } else {
                    notify('app.ancs.qrScan.notFound', 'warning');
                }
            } catch (err) {
                notify('app.ancs.qrScan.searchFailed', 'error');
            } finally {
                setProcessing(false);
            }
        },
        [find, setFilters, displayedFilters, filterValues, dispatch, notify],
    );

    return (
        <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {isQrFiltered && (
                <Button label="app.ancs.qrScan.clear" onClick={clearQrFilter}>
                    <ClearIcon />
                </Button>
            )}

            <Button
                label="app.ancs.qrScan.button"
                disabled={processing}
                onClick={() => setOpen(true)}
            >
                {processing ? <CircularProgress size={18} /> : <CenterFocusStrongIcon />}
            </Button>

            <QrScannerDialog open={open} onClose={() => setOpen(false)} onDecoded={handleDecoded} />
        </Box>
    );
};

export default QrScanButton;
